import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const PRIMARY = [
    { label: "Home", href: "/", type: "anchor" },
    { label: "Pricing", to: "/pricing", type: "route" },
    { label: "Compare", to: "/compare", type: "route" },
    { label: "About", to: "/about", type: "route" },
    { label: "For Startups", to: "/startups", type: "route" },
];

const RESOURCES = [

    // {
    //     label: "Docs",
    //     to: "/docs",
    //     d: "Module explainers, videos, usage steps.",
    // },
    {
        label: "Blog",
        to: "/blog",
        d: "Field notes from the operating layer.",
    },
    {
        label: "Use Cases",
        to: "/use-cases",
        d: "How teams actually use ApexBrain end-to-end.",
    },
    {
        label: "Case Studies",
        to: "/case-studies",
        d: "Real transformations across 5 themes.",
    },
   
   
    {
        label: "Roadmap",
        to: "/roadmap",
        d: "What we’re building next, votable.",
    },
    {
        label: "Changelog",
        to: "/changelog",
        d: "Every release, in the open.",
    },
   
    {
        label: "FAQs",
        to: "/faqs",
        d: "Common questions, instant answers.",
    },
    {
        label: "Help & Community",
        to: "/help",
        d: "Search, contact and 12k‑strong Discord.",
    },
   
   
    {
        label: "Security",
        to: "/security",
        d: "Encryption, identity, audits, residency.",
    },
    // {
    //     label: "Compliance",
    //     to: "/compliance",
    //     d: "GDPR · HIPAA · SOC 2 · ISO 27001.",
    // },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [resOpen, setResOpen] = useState(false);
    const resRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (resRef.current && !resRef.current.contains(e.target)) {
                setResOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`fixed inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "py-3" : "py-5"
            }`}
            style={{ top: "var(--site-announcement-h, 0px)" }}
            data-testid="site-nav"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div
                    className={`flex items-center justify-between rounded-2xl transition-all duration-500 overflow-visible ${
                        scrolled
                            ? "glass-strong px-4 sm:px-6 py-2.5"
                            : "px-2 py-2"
                    }`}
                >
                    <a
                        href="/"
                        className="flex items-center gap-2.5 group"
                        data-testid="nav-logo"
                    >
                        <img
                            src="/favicon.png"
                            alt="ApexBrain"
                            className="w-8 h-8 rounded-lg shadow-[0_0_18px_rgba(94,234,212,0.35)]"
                        />
                        <span className="font-vireon text-xl tracking-wide text-white">
                            Apex<span className="bg-gradient-to-r from-cyan-300 to-emerald-400 bg-clip-text text-transparent">Brain</span>
                        </span>
                    </a>

                    <nav
                        className="hidden md:flex items-center gap-7"
                        data-testid="nav-links"
                    >
                        {PRIMARY.map((l) =>
                            l.type === "route" ? (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    className="text-sm text-zinc-400 hover:text-white transition-colors duration-200 font-medium"
                                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    className="text-sm text-zinc-400 hover:text-white transition-colors duration-200 font-medium"
                                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </a>
                            ),
                        )}

                        {/* Resources dropdown */}
                        <div className="relative overflow-visible" ref={resRef}>
                            <button
                                onClick={() => setResOpen((v) => !v)}
                                className="text-sm text-zinc-400 hover:text-white transition-colors duration-200 font-medium inline-flex items-center gap-1"
                                data-testid="nav-resources-toggle"
                                aria-expanded={resOpen}
                            >
                                Resources
                                <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform ${resOpen ? "rotate-180" : ""}`}
                                />
                            </button>
                            <AnimatePresence>
                                {resOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 8 }}
                                        transition={{ duration: 0.18 }}
                                        className="absolute right-0 mt-3 w-[480px] z-[60] rounded-2xl p-3 border border-white/15 bg-obsidian-50/95 backdrop-blur-xl shadow-[0_24px_80px_-12px_rgba(0,0,0,0.85)]"
                                        data-testid="nav-resources-menu"
                                    >
                                        <div className="grid grid-cols-2 gap-1">
                                            {RESOURCES.map((r) => (
                                                <Link
                                                    key={r.label}
                                                    to={r.to}
                                                    onClick={() => setResOpen(false)}
                                                    className="block rounded-xl p-3 hover:bg-white/10 transition group"
                                                    data-testid={`nav-resource-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                >
                                                    <div className="text-sm font-semibold text-white group-hover:text-cyan-300 transition">
                                                        {r.label}
                                                    </div>
                                                    <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
                                                        {r.d}
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        <button
                            onClick={() => (window.location.href = "/contact-sales")}
                            className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-full transition"
                            data-testid="nav-signin-btn"
                        >
                            Contact sales
                        </button>
                        <Link
                            to="/pricing"
                            className="text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-5 py-2.5 rounded-full hover:brightness-110 transition"
                            data-testid="nav-cta-btn"
                        >
                            Purchase Now
                        </Link>
                    </div>

                    <button
                        className="md:hidden p-2 text-white"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        aria-expanded={open}
                    >
                        {open ? (
                            <X className="w-5 h-5" />
                        ) : (
                            <Menu className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="md:hidden mt-2 glass-strong rounded-2xl p-3 flex flex-col gap-1"
                        data-testid="nav-mobile-menu"
                    >
                        {PRIMARY.map((l) =>
                            l.type === "route" ? (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className="text-sm text-zinc-300 hover:text-white py-2.5 px-3 rounded-lg hover:bg-white/5"
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="text-sm text-zinc-300 hover:text-white py-2.5 px-3 rounded-lg hover:bg-white/5"
                                >
                                    {l.label}
                                </a>
                            ),
                        )}
                        <div className="mt-1 pt-3 border-t border-white/10">
                            <div className="px-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-code mb-1">
                                // resources
                            </div>
                            {RESOURCES.map((r) => (
                                <Link
                                    key={r.label}
                                    to={r.to}
                                    onClick={() => setOpen(false)}
                                    className="block text-sm text-zinc-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5"
                                >
                                    {r.label}
                                </Link>
                            ))}
                        </div>
                        <Link
                            to="/pricing"
                            onClick={() => setOpen(false)}
                            className="mt-2 block text-center text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow py-2.5 rounded-full"
                            data-testid="nav-mobile-cta"
                        >
                            Purchase Now
                        </Link>
                    </motion.div>
                )}
            </div>
        </motion.header>
    );
};
